import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Search, Tag, Layers, AlertCircle, ChevronRight, Calendar, Hash, Activity,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNowStrict } from 'date-fns';
import { useTickers, useThemes } from '../hooks/useMentions';
import { actionBadge } from './reportHelpers';
import WatchlistPinButton from './WatchlistPinButton';

/**
 * Mentions browser. Two sub-tabs:
 *   - Tickers (search + sort over symbols)
 *   - Themes  (search + sort over theme slugs)
 *
 * Each row links to /mentions/tickers/:symbol or /mentions/themes/:slug
 * for the detail view. Routes use kind+key path params so a single
 * MentionDetail component can handle both.
 */
const Mentions = () => {
  const { kind } = useParams();   // 'tickers' | 'themes' | undefined
  const navigate = useNavigate();
  const activeTab = kind === 'themes' ? 'themes' : 'tickers';

  const switchTab = (next) => navigate(`/mentions/${next}`);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Tickers &amp; Themes</h1>
          <p>Search and browse what Russ has been mentioning, and the latest guidance on each.</p>
        </div>
      </div>

      <div className="filter-buttons" style={{ marginBottom: '1rem' }}>
        <button
          className={`btn ${activeTab === 'tickers' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => switchTab('tickers')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
        >
          <Tag size={16} /> Tickers
        </button>
        <button
          className={`btn ${activeTab === 'themes' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => switchTab('themes')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
        >
          <Layers size={16} /> Themes
        </button>
      </div>

      {activeTab === 'tickers' ? <TickersList /> : <ThemesList />}
    </div>
  );
};

// --- Tickers list ---------------------------------------------------

const TickersList = () => {
  const navigate = useNavigate();
  const { tickers, loading, error } = useTickers(500);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('lastSeen'); // lastSeen | mentions | alpha

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    let list = tickers;
    if (q) {
      list = list.filter((t) =>
        t.symbol.includes(q) || (t.displayName || '').toUpperCase().includes(q));
    }
    if (sort === 'mentions') {
      list = [...list].sort((a, b) =>
        (b.messageMentionCount + b.callToActionCount + b.transcriptMentionCount) -
        (a.messageMentionCount + a.callToActionCount + a.transcriptMentionCount));
    } else if (sort === 'alpha') {
      list = [...list].sort((a, b) => a.symbol.localeCompare(b.symbol));
    }
    return list;
  }, [tickers, search, sort]);

  if (loading) return <Spinner />;
  if (error)   return <ErrorBlock message={error} />;

  return (
    <>
      <SearchBar
        placeholder="Search tickers... (e.g. NVDA)"
        value={search}
        onChange={setSearch}
      />
      <SortBar
        sort={sort}
        setSort={setSort}
        options={[
          { id: 'lastSeen', label: 'Most recent' },
          { id: 'mentions', label: 'Most mentioned' },
          { id: 'alpha',    label: 'A-Z' },
        ]}
        total={filtered.length}
        outOf={tickers.length}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {filtered.map((t) => (
          <TickerRow
            key={t.symbol}
            ticker={t}
            onClick={() => navigate(`/mentions/tickers/${encodeURIComponent(t.symbol)}`)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="empty-state">
            <Tag size={48} />
            <p>{search ? `No tickers matching "${search}"` : 'No tickers yet'}</p>
          </div>
        )}
      </div>
    </>
  );
};

const TickerRow = ({ ticker, onClick }) => {
  const lastSeen = parseDate(ticker.lastSeen);
  const lastReportDate = parseDate(ticker.latestReportDate);

  return (
    <button type="button" onClick={onClick} className="analysis-list-card mention-row">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
        <div style={{
          minWidth: 70, fontWeight: 700, color: 'var(--primary-color)',
          fontSize: '1.0625rem', flexShrink: 0,
        }}>
          {ticker.symbol}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Curated company name, when the security has an identity. Without
              it a row like "LNXSY" is unrecognisable at a glance. */}
          {ticker.displayName && (
            <div style={{
              fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.125rem',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {ticker.displayName}
            </div>
          )}

          {/* Latest guidance row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.25rem' }}>
            {ticker.latestAction ? (
              <>
                <span className={`badge ${actionBadge(ticker.latestAction)}`}>
                  {ticker.latestAction}
                </span>
                {ticker.latestConfidence && (
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    {ticker.latestConfidence} conviction
                  </span>
                )}
                {lastReportDate && (
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    {format(lastReportDate, 'MMM dd')}
                  </span>
                )}
              </>
            ) : ticker.lastDirectMention ? (
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                Last direct mention {ticker.lastDirectMention.sourceType === 'transcript' ? 'in transcript' : 'in message'}
              </span>
            ) : (
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                No mentions yet
              </span>
            )}
          </div>

          {/* Counts row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.875rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            <CountStat icon={<Hash size={12} />} label="msgs" value={ticker.messageMentionCount} />
            <CountStat icon={<Activity size={12} />} label="actions" value={ticker.callToActionCount} />
            {ticker.transcriptMentionCount > 0 && (
              <CountStat icon={<Layers size={12} />} label="transcripts" value={ticker.transcriptMentionCount} />
            )}
            {lastSeen && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <Calendar size={12} /> {formatDistanceToNowStrict(lastSeen, { addSuffix: true })}
              </span>
            )}
          </div>
        </div>

        <WatchlistPinButton kind="ticker" itemKey={ticker.symbol} compact />
        <ChevronRight size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
      </div>
    </button>
  );
};

// --- Themes list ----------------------------------------------------

const ThemesList = () => {
  const navigate = useNavigate();
  const { themes, loading, error } = useThemes(500);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('lastSeen');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = themes;
    if (q) {
      list = list.filter((t) =>
        (t.slug || '').toLowerCase().includes(q) ||
        (t.displayName || '').toLowerCase().includes(q));
    }
    if (sort === 'mentions') {
      list = [...list].sort((a, b) =>
        (b.messageMentionCount + b.transcriptMentionCount + b.reportThemeCount) -
        (a.messageMentionCount + a.transcriptMentionCount + a.reportThemeCount));
    } else if (sort === 'alpha') {
      list = [...list].sort((a, b) => (a.displayName || a.slug).localeCompare(b.displayName || b.slug));
    }
    return list;
  }, [themes, search, sort]);

  if (loading) return <Spinner />;
  if (error)   return <ErrorBlock message={error} />;

  return (
    <>
      <SearchBar
        placeholder="Search themes... (e.g. AI infrastructure)"
        value={search}
        onChange={setSearch}
      />
      <SortBar
        sort={sort}
        setSort={setSort}
        options={[
          { id: 'lastSeen', label: 'Most recent' },
          { id: 'mentions', label: 'Most mentioned' },
          { id: 'alpha',    label: 'A-Z' },
        ]}
        total={filtered.length}
        outOf={themes.length}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {filtered.map((th) => (
          <ThemeRow
            key={th.slug}
            theme={th}
            onClick={() => navigate(`/mentions/themes/${encodeURIComponent(th.slug)}`)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="empty-state">
            <Layers size={48} />
            <p>{search ? `No themes matching "${search}"` : 'No themes yet'}</p>
          </div>
        )}
      </div>
    </>
  );
};

const ThemeRow = ({ theme, onClick }) => {
  const lastSeen = parseDate(theme.lastSeen);
  const lastReportDate = parseDate(theme.latestReportDate);

  return (
    <button type="button" onClick={onClick} className="analysis-list-card mention-row">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.875rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <strong style={{ color: 'var(--text-primary)', fontSize: '0.9375rem' }}>
              {theme.displayName || theme.slug}
            </strong>
            <code style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{theme.slug}</code>
          </div>

          {theme.latestReportTitle && (
            <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>
              <em>Latest in report:</em> {theme.latestReportTitle}
              {lastReportDate && (
                <span style={{ marginLeft: '0.5rem', color: 'var(--text-secondary)' }}>
                  {format(lastReportDate, 'MMM dd')}
                </span>
              )}
            </p>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.875rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            <CountStat icon={<Hash size={12} />} label="msgs" value={theme.messageMentionCount} />
            <CountStat icon={<Activity size={12} />} label="reports" value={theme.reportThemeCount} />
            {theme.transcriptMentionCount > 0 && (
              <CountStat icon={<Layers size={12} />} label="transcripts" value={theme.transcriptMentionCount} />
            )}
            {lastSeen && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <Calendar size={12} /> {formatDistanceToNowStrict(lastSeen, { addSuffix: true })}
              </span>
            )}
          </div>
        </div>

        <WatchlistPinButton kind="theme" itemKey={theme.slug} label={theme.displayName || theme.slug} compact />
        <ChevronRight size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
      </div>
    </button>
  );
};

// --- Shared bits ----------------------------------------------------

const SearchBar = ({ placeholder, value, onChange }) => (
  <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
    <Search size={16} style={{
      position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)',
      color: 'var(--text-secondary)', pointerEvents: 'none',
    }} />
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '0.625rem 0.75rem 0.625rem 2.25rem',
        fontSize: '0.9375rem',
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        boxSizing: 'border-box',
      }}
    />
  </div>
);

const SortBar = ({ sort, setSort, options, total, outOf }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem',
  }}>
    <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
      {options.map((o) => (
        <button
          key={o.id}
          className={`btn ${sort === o.id ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setSort(o.id)}
          style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
        >
          {o.label}
        </button>
      ))}
    </div>
    <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
      {total === outOf ? `${outOf} total` : `${total} of ${outOf}`}
    </span>
  </div>
);

const CountStat = ({ icon, label, value }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
    {icon}
    <strong style={{ color: 'var(--text-primary)' }}>{value}</strong> {label}
  </span>
);

const Spinner = () => (
  <div className="loading"><div className="spinner"></div></div>
);

const ErrorBlock = ({ message }) => (
  <div className="error">
    <AlertCircle size={24} />
    <p>{message}</p>
  </div>
);

const parseDate = (raw) => {
  if (!raw) return null;
  try {
    return typeof raw === 'string' ? parseISO(raw) : new Date(raw);
  } catch {
    return null;
  }
};

export default Mentions;
